import { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";

const LARP_PATH = { de: "larp-erleben", en: "experience-larp" };
const SPENDEN_PATH = { de: "spenden", en: "donate" };
const ABOUT_PATH = { de: "ueber-uns", en: "about" };
const ORGAS_PATH = { de: "fuer-orgas", en: "for-organizers" };
const KONTAKT_PATH = { de: "kontakt", en: "contact" };
const JUGENDARBEIT_PATH = { de: "jugendarbeit", en: "youth-work" };

const linkStyle = {
  fontSize: "14px",
  padding: "9px 16px",
  borderRadius: "var(--radius-pill)",
};

/** Shared by the desktop dropdown and the mobile flat list — path/title/subtitle per item. */
function useAboutMenuItems(lang) {
  const { t } = useTranslation();
  return [
    { to: `/${lang}/${ABOUT_PATH[lang]}`, title: t("nav.aboutMenu.wer.title"), subtitle: t("nav.aboutMenu.wer.subtitle") },
    { to: `/${lang}/${ABOUT_PATH[lang]}/${ORGAS_PATH[lang]}`, title: t("nav.aboutMenu.orgas.title"), subtitle: t("nav.aboutMenu.orgas.subtitle") },
    { to: `/${lang}/${JUGENDARBEIT_PATH[lang]}`, title: t("nav.aboutMenu.jugendarbeit.title"), subtitle: t("nav.aboutMenu.jugendarbeit.subtitle") },
    { to: `/${lang}/${KONTAKT_PATH[lang]}`, title: t("nav.aboutMenu.infos.title"), subtitle: t("nav.aboutMenu.infos.subtitle") },
  ];
}

/** Desktop-only: "Über uns" as a click-to-open dropdown (label still links directly; the caret toggles the panel). */
function AboutDropdown({ lang }) {
  const { t } = useTranslation();
  const items = useAboutMenuItems(lang);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    function handlePointerDown(event) {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    }
    function handleKeyDown(event) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    <li
      ref={ref}
      style={{ position: "relative", display: "flex", alignItems: "center" }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <NavLink
        to={`/${lang}/${ABOUT_PATH[lang]}`}
        end
        style={({ isActive }) => ({ ...linkStyle, color: isActive ? "var(--color-cream)" : "var(--color-cream-soft)" })}
      >
        {t("nav.ueberUns")}
      </NavLink>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={t("nav.ueberUns")}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: "24px",
          minHeight: "24px",
          marginLeft: "-10px",
          background: "none",
          border: "none",
          padding: "2px",
          cursor: "pointer",
          color: "var(--color-cream-soft)",
          fontSize: "14px",
        }}
      >
        ▾
      </button>
      {open && (
        <div style={{ position: "absolute", top: "100%", left: 0, paddingTop: "8px", minWidth: "230px", zIndex: 30 }}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              background: "var(--color-bg-raised)",
              border: "1px solid rgba(254,250,239,0.12)",
              borderRadius: "12px",
              padding: "8px",
              boxShadow: "0 18px 40px rgba(0,0,0,0.35)",
            }}
          >
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end
                onClick={() => setOpen(false)}
                className="tl-nav-dropdown-item"
                style={{ display: "flex", flexDirection: "column", gap: "2px", padding: "11px 14px", borderRadius: "8px", textDecoration: "none" }}
              >
                <span style={{ fontSize: "14px", color: "var(--color-cream)" }}>{item.title}</span>
                <span style={{ fontSize: "12px", color: "var(--color-cream-faint)" }}>{item.subtitle}</span>
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </li>
  );
}

/** `vertical`/`onNavigate` power the mobile burger menu (Header.jsx) — same links, stacked, closing the menu on tap. */
export default function Navigation({ lang, vertical = false, onNavigate }) {
  const { t } = useTranslation();
  const aboutMenuItems = useAboutMenuItems(lang);

  const links = [
    { to: `/${lang}/`, label: t("nav.home"), end: true },
    { to: `/${lang}/${SPENDEN_PATH[lang]}`, label: t("nav.spenden") },
    { to: `/${lang}/events`, label: t("nav.events") },
    { to: `/${lang}/${LARP_PATH[lang]}`, label: t("nav.larpErleben") },
  ];

  return (
    <nav aria-label="Main navigation">
      <ul
        style={{
          display: "flex",
          // Only set explicitly for the vertical (mobile menu) case — "row"
          // is flexbox's own default, and explicitly stating it anyway
          // alongside flexWrap below gives Chrome's CSSOM two adjacent
          // longhand values it consolidates into a `flex-flow` shorthand
          // when serializing outerHTML for the prerender step, which then
          // reads as a hydration mismatch against React's own (correctly
          // un-consolidated) longhand output.
          flexDirection: vertical ? "column" : undefined,
          gap: vertical ? "2px" : "8px",
          alignItems: vertical ? "stretch" : "center",
          flexWrap: vertical ? "nowrap" : "wrap",
          listStyle: "none",
          margin: 0,
          padding: 0,
        }}
      >
        {links.map((link) => (
          <li key={link.to}>
            <NavLink
              to={link.to}
              end={link.end}
              onClick={onNavigate}
              style={({ isActive }) => ({
                ...linkStyle,
                display: vertical ? "flex" : undefined,
                alignItems: vertical ? "center" : undefined,
                minHeight: vertical ? "44px" : undefined,
                color: isActive ? "var(--color-cream)" : "var(--color-cream-soft)",
              })}
            >
              {link.label}
            </NavLink>
          </li>
        ))}

        {vertical ? (
          <li style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            <span style={{ ...linkStyle, display: "block", color: "var(--color-cream)" }}>{t("nav.ueberUns")}</span>
            {aboutMenuItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end
                onClick={onNavigate}
                style={({ isActive }) => ({
                  ...linkStyle,
                  display: "flex",
                  alignItems: "center",
                  minHeight: "44px",
                  paddingLeft: "28px",
                  color: isActive ? "var(--color-cream)" : "var(--color-cream-soft)",
                })}
              >
                {item.title}
              </NavLink>
            ))}
          </li>
        ) : (
          <AboutDropdown lang={lang} />
        )}

        <li>
          <NavLink
            to={`/${lang}/${SPENDEN_PATH[lang]}`}
            className="tl-pill tl-pill-primary"
            onClick={onNavigate}
            style={{ display: vertical ? "inline-flex" : undefined, marginLeft: vertical ? 0 : "4px", marginTop: vertical ? "10px" : 0 }}
          >
            {t("nav.jetztSpenden")}
          </NavLink>
        </li>
      </ul>
    </nav>
  );
}
